import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  HelpCircle,
  Lock,
  X,
  Archive,
  Bell,
  BookOpen,
  Bookmark,
  Bot,
  CalendarClock,
  ClipboardList,
  Cookie,
  CreditCard,
  Crown,
  FileCheck,
  FileText,
  Fingerprint,
  FlaskConical,
  Gauge,
  GitCompare,
  Globe,
  HeartPulse,
  Inbox,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  ListTree,
  Megaphone,
  MousePointerClick,
  Network,
  Palette,
  Plug,
  Radio,
  Receipt,
  RotateCcw,
  Route,
  ScrollText,
  Send,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  Sigma,
  Store,
  Tag,
  Target,
  TrendingUp,
  UserCog,
  Users,
  Waypoints,
  Zap,
  Bug,
} from 'lucide-react';
import { useT } from '../i18n';
import { useMobileSidebar } from '../App';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import usePlanAccess from '../hooks/usePlanAccess';
import BRAND from '../brand';

/*
  Shared xpnsr.tech sidebar (matches auth.html / dashboard.html on xpnsr.tech).
  Used by AuthForm (no app navigation) and the authenticated app shell.

  Cross-product links go to the live deployments:
    - Expense  -> https://app.xpnsr.tech
    - CBN      -> https://xpnsr.tech (marketing / SEO landing)
    - Tracker  -> current app (this product)

  Authenticated nav (`authedNav`) is a flat list where each item carries a
  `group` key. The sidebar buckets items by group at render time so the user
  scans a handful of small sections instead of a 27-item wall.
*/

const PRODUCTS = [
  { key: 'xp', label: 'Expense', href: 'https://app.xpnsr.tech', kbd: '⌘1' },
  { key: 'cbn', label: 'CBN', href: 'https://seo.xpnsr.tech', kbd: '⌘2' },
  { key: 'trk', label: 'Tracker', href: '/', kbd: '⌘3' },
];

const NAV_HINTS = {
  'Dashboard': 'Overview of clicks, conversions and active campaigns',
  'Campaigns': 'Create ad campaigns and get tracking links',
  'Landing pages': 'Upload landing pages (ZIP) or set a URL for split-tests',
  'Pre-Landings': 'Warm-up pages shown before the offer',
  'Ad accounts': 'Connect Facebook, Google, TikTok ad accounts',
  'Cost entries': 'Manually record campaign spend — flat fees, offline media, corrections',
  'Security': 'Two-factor auth, sessions and password',
  'Offers': 'Uploaded offers/funnels + API routing to partners (weights, failover) + lead forms',
  'White pages': 'Static pages for bots/ad review — hosted here, served same-origin on your domain (Google-safe cloaking)',
  'Leads': 'Stored form leads: name/email/phone/geo, status (synced from the partner), filter + CSV export for resale',
  'Sources': 'Traffic sources with presets (Google Ads with tm & macros, etc.)',
  'Affiliate networks': 'Partner network presets: the postback URL to paste into the network, the click ID parameter for the offer link, per-network reports',
  'Traffic': 'Manage flows and traffic distribution rules',
  'A/B Tests': 'Split-test landings and offers with auto-distribution',
  'Anti-fraud': 'Block bots, fraud and suspicious clicks',
  'TLS fingerprints': 'JA3 / JA4 fingerprint overrides for bot detection',
  'Bot lists': 'Custom bot IPs/CIDRs and UA signatures for the bot-detection filter',
  'Analytics': 'Detailed reports: charts, geo, devices, ROI',
  'Trends': 'Metrics over time buckets (hour/day/weekday/month) + conversion-date view',
  'Clicks log': 'Full click log with IP, UserAgent, GEO and time',
  'Sub-ID reports': 'Drill-down by sub1–sub15: clicks, conversions, CR',
  'Custom metrics': 'Formula-based report columns over base metrics (CR, EPC, ROI variants)',
  'Saved reports': 'Reusable report definitions, exportable to CSV/XLSX on demand',
  'Scheduled reports': 'Recurring reports emailed on a daily/weekly/monthly cadence',
  'Compare': 'Side-by-side metrics for multiple campaigns',
  'Automation': 'Auto-rules: pause campaigns on low ROI, etc.',
  'Integrations': 'Partner CRMs and networks: leads sent via API (ready-made adapters or Custom API), status sync, webhooks',
  'Domains': 'Add your own domains for tracking links',
  'Conversion API': 'Online conversions to Google/FB/TikTok (S2S). Google — simple, no OAuth',
  'Conversion types': 'Custom conversion types: map network statuses, reporting flags and transition rules',
  'API keys': 'Keys to access the tracker REST API',
  'Workspace': 'Invite your team, assign roles and permissions',
  'Audit log': 'Action log of all users for 90 days',
  'Consent banner': 'Cookie consent banner for GDPR compliance',
  'Marketplace': 'Ready-made templates and integrations',
  'API docs': 'Swagger REST API docs (opens in new tab)',
  'Documentation': 'Step-by-step user guide for every feature',
  'CAPI DLQ': 'Failed conversion dispatches — can be retried',
  'System health': 'Server, queue and database status',
  'System status': 'Uptime, DB size, data-processing queue and host metrics',
  'S2S log': 'Outbound conversion postbacks fired across your campaigns',
  'GDPR / DSAR': 'Data-subject export (Art. 15) and erasure (Art. 17) by identifier',
  'Billing': 'Plan, payment and usage limits',
  'Settings': 'Profile, language, timezone and notifications',
};

// Display order for nav groups + their human labels. Anything without a
// `group` falls into the legacy 'all' bucket so older callers still work.
// Nav entries that depend on a plan feature. Anything absent here is open on
// every edition. The server is still the authority; this only saves the user
// a wasted trip into a screen they cannot use yet.
const NAV_FEATURE = {
  'Automation': 'automation',
  'Custom metrics': 'custom_metrics',
  'Conversion types': 'conversion_types',
  'API keys': 'click_api',
  'Marketplace': 'marketplace',
  'TLS fingerprints': 'tls_fingerprints',
  'Audit log': 'audit_log',
  'Leads': 'lead_forms',
  'Integrations': 'lead_forms',
  'Conversion API': 'capi_all',
  'Ad accounts': 'cost_sync',
  'Scheduled reports': 'scheduled_reports',
};

// One icon per section, the same set the site's dashboard mock shows.
const NAV_ICONS = {
  '/dashboard': LayoutDashboard,
  '/analytics': Gauge,
  '/reports': FileText,
  '/custom-metrics': Sigma,
  '/reports/saved': Bookmark,
  '/reports/scheduled': CalendarClock,
  '/clicks/log': MousePointerClick,
  '/reports/sub-ids': ListTree,
  '/analytics/trends': TrendingUp,
  '/campaigns/compare': GitCompare,
  '/campaigns': Megaphone,
  '/landing-pages': LayoutTemplate,
  '/pre-landings': Layers,
  '/offers': Tag,
  '/white-pages': FileCheck,
  '/leads': Inbox,
  '/traffic-sources': Radio,
  '/affiliate-networks': Network,
  '/traffic': Route,
  '/ab-testing': FlaskConical,
  '/anti-fraud': ShieldCheck,
  '/anti-fraud/tls': Fingerprint,
  '/settings/bot-lists': Bot,
  '/automation': Zap,
  '/conversion-api': Send,
  '/conversion-types': Target,
  '/domains': Globe,
  '/integrations': Plug,
  '/marketplace': Store,
  '/consent-banner': Cookie,
  '/logs/s2s': ScrollText,
  '/diagnostics/click-traces': Waypoints,
  '/workspace': Users,
  '/archive': Archive,
  '/workspace/access': UserCog,
  '/ad-account-connections': Link2,
  '/cost-entries': Receipt,
  '/api-keys': KeyRound,
  '/docs': BookOpen,
  '/settings/security': Lock,
  '/admin/audit': ClipboardList,
  '/settings/gdpr': Shield,
  '/admin/platform': Crown,
  '/admin/capi-dlq': RotateCcw,
  '/admin/errors': Bug,
  '/admin/health': HeartPulse,
  '/admin/system': Server,
  '/billing': CreditCard,
  '/self-hosted': Server,
  '/workspace/branding': Palette,
  '/notifications/preferences': Bell,
  '/settings': Settings,
};

const NAV_GROUPS = [
  { key: 'launch', label: 'Launch' },
  { key: 'partners', label: 'Partners and conversions' },
  { key: 'stats', label: 'Statistics' },
  { key: 'protection', label: 'Protection' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'admin', label: 'Admin' },
  { key: 'account', label: 'Account' },
];

function bucketByGroup(items) {
  const buckets = new Map();
  for (const item of items) {
    const g = item.group || 'all';
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g).push(item);
  }
  // Preserve declared group order, fall back to 'all' for ungrouped legacy
  // callers so the old flat shape still renders.
  const ordered = [];
  for (const g of NAV_GROUPS) {
    if (buckets.has(g.key)) ordered.push({ ...g, items: buckets.get(g.key) });
  }
  if (buckets.has('all')) ordered.push({ key: 'all', label: '', items: buckets.get('all') });
  return ordered;
}

export default function XpnsrSidebar({ authedNav, footer }) {
  const { t } = useT();
  const location = useLocation();
  const [productOpen, setProductOpen] = useState(false);
  const [hintsEnabled, setHintsEnabled] = useState(() => {
    try { return localStorage.getItem('xp_nav_hints') !== 'off'; } catch { return true; }
  });

  // Per-group collapse state. Defaults are loaded from localStorage so the
  // user's choice sticks across reloads. All groups are expanded by
  // default — flipping links from "always visible" to "hidden until you
  // click" is a regression for muscle memory; users can collapse the
  // sections they don't care about and the choice persists.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('xp_nav_groups_collapsed');
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    // null = no stored choice yet, every section closed (LetsCampaign default).
    return BRAND.navGroupsClosedByDefault ? null : {};
  });

  const toggleHints = () => {
    const next = !hintsEnabled;
    setHintsEnabled(next);
    try { localStorage.setItem('xp_nav_hints', next ? 'on' : 'off'); } catch {}
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      // No stored preference yet means every section is closed, so the first
      // click on a heading must open that one rather than close it again.
      const base = prev || {};
      const current = prev === null ? true : !!base[key];
      const next = { ...base, [key]: !current };
      try { localStorage.setItem('xp_nav_groups_collapsed', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const groups = authedNav && authedNav.length > 0 ? bucketByGroup(authedNav) : [];
  // Exactly ONE nav item is active: the most specific (longest-href) match for
  // the current path. Prefix matching alone lit up a parent ("Reports")
  // alongside its child ("Scheduled reports") on nested routes.
  const activeHref = groups
    .flatMap((g) => g.items)
    .filter(
      (it) =>
        !it.external &&
        it.href &&
        (location.pathname === it.href ||
          (it.href !== '/' && location.pathname.startsWith(it.href + '/')))
    )
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
  const mobileSidebar = useMobileSidebar();
  const mobileOpen = mobileSidebar?.open ?? false;
  const planAccess = usePlanAccess();

  // Close mobile sidebar on route change
  useEffect(() => {
    if (mobileSidebar) mobileSidebar.close();
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <TooltipProvider delayDuration={350}>
      {mobileOpen && (
        <div
          className="xp-mobile-overlay"
          onClick={() => mobileSidebar.close()}
          data-testid="mobile-overlay"
        />
      )}
      <aside className={`xp-aside${mobileOpen ? ' xp-aside--mobile-open' : ''}`}>
        {mobileOpen && (
          <button
            type="button"
            className="xp-mobile-close"
            onClick={() => mobileSidebar.close()}
            aria-label={t('Close navigation')}
            data-testid="mobile-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <Link to="/" className="xp-brand">
          {BRAND.logoMark ? <BRAND.logoMark className="xp-brand-mark" /> : <span className="xp-brand-dot" />}
          <span className="xp-brand-logo">{BRAND.sidebarLogo}</span>
        </Link>

        {/* Product switcher — collapsed by default. Only brands with sibling products show it. */}
        {BRAND.productSwitcher && (
        <div className="xp-section xp-section--collapsible">
          <button
            type="button"
            className="xp-section-toggle"
            onClick={() => setProductOpen((v) => !v)}
            aria-expanded={productOpen}
            data-testid="product-switcher-toggle"
          >
            <span className="xp-section-label">// product</span>
            <ChevronDown className={`xp-section-chevron ${productOpen ? 'is-open' : ''}`} />
          </button>
          {productOpen && (
            <div className="xp-switch xp-switch--animated">
              {PRODUCTS.map((p) => {
                const isOn = p.key === 'trk';
                const className = `xp-switch-btn${isOn ? ' is-on' : ''}`;
                const inner = (
                  <>
                    <span className={`xp-switch-ind d-${p.key}`} />
                    {p.label}
                    <span className="xp-switch-kbd">{p.kbd}</span>
                  </>
                );
                return p.href.startsWith('http') ? (
                  <a
                    key={p.key}
                    href={p.href}
                    className={className}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-switch-${p.key}`}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link
                    key={p.key}
                    to={p.href}
                    className={className}
                    data-testid={`product-switch-${p.key}`}
                  >
                    {inner}
                  </Link>
                );
              })}
            </div>
          )}
          {!productOpen && (
            <div className="xp-switch-collapsed">
              <span className="xp-switch-ind d-trk" />
              <span className="xp-switch-collapsed-label">Tracker</span>
            </div>
          )}
        </div>
        )}

        {/* App / page navigation — only shown when authenticated */}
        {groups.length > 0 && (
          <div className="xp-nav-group">
            <div className="xp-nav-header">
              <h6>{BRAND.terminalLabels ? '// navigate' : t('Navigate')}</h6>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`xp-hints-toggle ${hintsEnabled ? 'is-on' : ''}`}
                    onClick={toggleHints}
                    aria-label={t('Toggle navigation hints')}
                    data-testid="hints-toggle"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">{t(hintsEnabled ? 'Hide hints' : 'Show hints')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="xp-nav-list">
              {groups.map((group) => {
                const containsActive = group.items.some((item) => item.href === activeHref);
                // Always show the section that contains the current route,
                // even if the user previously collapsed it — otherwise the
                // active link silently disappears from the sidebar.
                const stored = collapsedGroups === null ? true : !!collapsedGroups[group.key];
                const collapsed = stored && !containsActive;
                const showHeading = group.label && group.key !== 'all';

                return (
                  <div key={group.key} className="xp-nav-section" data-testid={`nav-section-${group.key}`}>
                    {showHeading ? (
                      <button
                        type="button"
                        className={`xp-nav-section-toggle${collapsed ? '' : ' is-open'}`}
                        onClick={() => toggleGroup(group.key)}
                        aria-expanded={!collapsed}
                        data-testid={`nav-section-toggle-${group.key}`}
                      >
                        <span>{t(group.label)}</span>
                        <ChevronDown className={`xp-nav-section-chev ${collapsed ? '' : 'is-open'}`} />
                      </button>
                    ) : null}
                    {(!showHeading || !collapsed) && group.items.map((item) => {
                      const isOn = item.href === activeHref;
                      const Ico = NAV_ICONS[item.href];
                      const icon = Ico ? <Ico className="xp-nav-ico" aria-hidden="true" /> : null;
                      const testId = `nav-${item.key.toLowerCase().replace(/\s+/g, '-')}`;
                      const hintKey = NAV_HINTS[item.key];

                      const needed = NAV_FEATURE[item.key];
                      const locked =
                        Boolean(needed) &&
                        !planAccess.loading &&
                        (needed === 'scheduled_reports'
                          ? planAccess.limit('scheduled_reports') === 0
                          : !planAccess.allows(needed));

                      if (locked) {
                        return (
                          <Tooltip key={item.key}>
                            <TooltipTrigger asChild>
                              <Link
                                to="/billing"
                                className={`xp-nav-link is-locked${Ico ? ' has-ico' : ''}`}
                                data-testid={testId + '-locked'}
                              >
                                {icon}
                                {t(item.key)}
                                <Lock className="xp-nav-lock" />
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="xp-nav-tooltip" sideOffset={10}>
                              <p>{t('Not included on your plan. Open Billing to upgrade.')}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      const linkEl = item.external ? (
                        <a
                          key={item.key}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`xp-nav-link${Ico ? ' has-ico' : ''}`}
                          data-testid={testId}
                        >
                          {icon}
                          {t(item.key)}
                        </a>
                      ) : (
                        <Link
                          key={item.key}
                          to={item.href}
                          className={`xp-nav-link${isOn ? ' is-on' : ''}${Ico ? ' has-ico' : ''}`}
                          data-testid={testId}
                          aria-current={isOn ? 'page' : undefined}
                        >
                          {icon}
                          {t(item.key)}
                        </Link>
                      );

                      if (hintsEnabled && hintKey) {
                        return (
                          <Tooltip key={item.key}>
                            <TooltipTrigger asChild>
                              {linkEl}
                            </TooltipTrigger>
                            <TooltipContent side="right" className="xp-nav-tooltip" sideOffset={10}>
                              <p>{t(hintKey)}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      return linkEl;
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {footer ? footer : (
          <a
            href={BRAND.ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="xp-sidebar-cta"
            data-testid="book-demo-cta"
          >
            {t(BRAND.ctaLabel)}
          </a>
        )}
      </aside>
    </TooltipProvider>
  );
}
